import { CurrencyFormatDisplay } from 'components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { formatDate3 } from '../../../../../../utils';

interface IProjectOverviewSection2 {
  eligibleCost: number;
  id: string;
  ineligibleCost: number;
  projectFundingPercentage: number;
  rate: number;
  source: string;
  term: string;
  totalCost: number;
}

interface IProjectOverviewProps {
  step3: {
    beginDate: string;
    completionDate: string;
    hasPortion: string;
    portionDescription: string;
    projectDescription: string;
    section2: Array<IProjectOverviewSection2>;
  };
}
export default function ProjectOverview({ step3 }: IProjectOverviewProps) {
  const totalEligibleCost = step3?.section2?.reduce(
    (acc: number, curr: IProjectOverviewSection2) => {
      return (acc += curr?.eligibleCost);
    },
    0
  );

  const totalIneligibleCost = step3?.section2?.reduce(
    (acc: number, curr: IProjectOverviewSection2) => {
      return (acc += curr?.ineligibleCost);
    },
    0
  );

  const totalCosts = step3?.section2?.reduce(
    (acc: number, curr: IProjectOverviewSection2) => {
      return (acc += curr?.totalCost);
    },
    0
  );
  return (
    <>
      <section aria-labelledby='applicant-information-title'>
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='py-5'>
            <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
              <h2
                id='applicant-information-title'
                className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'
              >
                Project Overview
              </h2>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6 flex flex-col gap-3'>
            <div>
              <h2 className='mt-1 max-w-2xl '>
                Applicant / Business / Borrower Information
              </h2>
              <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2'>
                <div className='col-span-2'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Project Description
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {step3?.projectDescription}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Project Begin Date
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {formatDate3(step3?.beginDate)}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Project Completion Date
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900'>
                    {formatDate3(step3?.completionDate)}
                  </dd>
                </div>
                <div className='sm:col-span-1'>
                  <dt className='text-sm font-medium text-gray-500'>
                    Has project been started yet?
                  </dt>
                  <dd className='mt-1 text-sm text-gray-900 capitalize'>
                    {step3?.hasPortion}
                  </dd>
                </div>
                {step3?.hasPortion === 'yes' && (
                  <div className='col-span-2'>
                    <dt className='text-sm font-medium text-gray-500'>
                      Description of Started Project
                    </dt>
                    <dd className='mt-1 text-sm text-gray-900'>
                      {step3?.portionDescription ?? 'None'}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            <div>
              <h2 className='max-w-2xl '>Proposed Financing</h2>
              <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter overflow-x-scroll'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-100'>
                    <tr className='flex justify-between px-5 py-4'>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-40'>
                        Source
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-32'>
                        Rate
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-32'>
                        Term
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-32'>
                        Eligible Cost
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-32'>
                        Ineligible Cost
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-24'>
                        Total Costs
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {step3?.section2.map(
                      (finance: IProjectOverviewSection2) => (
                        <tr
                          key={finance.id}
                          className='flex items-center w-full justify-between px-5 py-2'
                        >
                          <td className='w-40 capitalize'>{finance?.source}</td>
                          <td className='w-32 capitalize'>
                            {finance?.rate ?? 0}
                          </td>
                          <td className='w-32 capitalize'>{finance?.term}</td>
                          <td className='w-32 capitalize'>
                            <CurrencyFormatDisplay
                              value={finance?.eligibleCost ?? 0}
                            />
                          </td>
                          <td className='w-32 capitalize'>
                            <CurrencyFormatDisplay
                              value={finance?.ineligibleCost ?? 0}
                            />
                          </td>
                          <td className='w-24 capitalize'>
                            <CurrencyFormatDisplay
                              value={finance?.totalCost ?? 0}
                            />
                          </td>
                        </tr>
                      )
                    )}
                    <tr className='flex items-center w-full justify-between px-5 py-2'>
                      <td className='w-40 font-bold'>Total</td>
                      <td className='w-32'></td>
                      <td className='w-32'></td>
                      <td className='w-32 font-bold'>
                        <CurrencyFormatDisplay value={totalEligibleCost ?? 0} />
                      </td>
                      <td className='w-32 font-bold'>
                        <CurrencyFormatDisplay
                          value={totalIneligibleCost ?? 0}
                        />
                      </td>
                      <td className='w-24 font-bold'>
                        <CurrencyFormatDisplay value={totalCosts ?? 0} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
